import { defineStore } from 'pinia'
import {
  discardTimer,
  getRunningTimer,
  startTimer,
  stopTimer,
} from '@/api/v2/task_time_entries'
import type { RunningTimer } from '@/models/TaskTimeEntry'

interface TimerState {
  running: RunningTimer | null
  loaded: boolean
}

/**
 * The one running timer of the current user.
 *
 * Held centrally because two places need it at once: the navbar widget and whichever ticket is
 * open. The elapsed time is derived from `started_at` on every read rather than counted up in
 * state, so a reload, a sleeping laptop or a second browser tab all show the same number.
 */
export const useTimerStore = defineStore('timer', {
  state: (): TimerState => ({
    running: null,
    loaded: false,
  }),

  getters: {
    isRunningForTask: (state) => (task_id: string): boolean =>
      state.running !== null && state.running.task_id === task_id,
  },

  actions: {
    resetState() {
      this.running = null
      this.loaded = false
    },

    async load(): Promise<RunningTimer | null> {
      try {
        const resp = await getRunningTimer()
        this.running = resp.data.data ?? null
      } catch {
        // A missing feature flag answers 403 — that is "no timer", not an error worth a toast.
        this.running = null
      }
      this.loaded = true
      return this.running
    },

    /**
     * Starts a timer. Returns the timer that is already running when the backend refuses with 422,
     * so the caller can ask before throwing away somebody's tracked time.
     */
    async start(
      task_id: string,
      description?: string | null,
    ): Promise<{ started: RunningTimer | null; conflict: RunningTimer | null }> {
      try {
        const resp = await startTimer(task_id, description)
        this.running = resp.data.data
        return { started: this.running, conflict: null }
      } catch (err: any) {
        const conflict = err?.response?.status === 422
          ? (err.response.data?.running_timer ?? null)
          : null
        if (conflict) {
          this.running = conflict
          return { started: null, conflict }
        }
        throw err
      }
    },

    async stop(
      task_id: string,
      description?: string | null,
      billing?: { billing_contact_id?: string | null; discount_percent?: number },
    ) {
      const resp = await stopTimer(task_id, description, billing)
      this.running = null
      return resp.data.data
    },

    async discard(task_id: string) {
      await discardTimer(task_id)
      this.running = null
    },
  },
})
